import { z } from 'zod'

// Client type
export type ClientType = 'professionnel' | 'particulier'

// Client interface
export interface Client {
  id: string
  created_at: string
  updated_at: string
  email: string
  full_name: string
  phone: string | null
  avatar_url: string | null
  type_client: ClientType
  company: string | null
  siret: string | null
  tva_intracommunautaire: string | null
  adresse_facturation: string | null
  role: 'client'
  // Relations
  projects?: Project[]
  factures?: Facture[]
  devis?: Devis[]
  documents?: Document[]
  assigned_admins?: ClientAdminAssignment[]
  logs?: NotificationLog[]
  // Computed
  projects_count?: number
  factures_impayees?: number
}

export interface Project {
  id: string
  created_at: string
  nom: string
  description: string | null
  status: 'draft' | 'active' | 'completed' | 'cancelled'
  client_id: string
  budget: number | null
  date_debut: string | null
  date_fin: string | null
}

export interface Facture {
  id: string
  created_at: string
  numero: string
  client_id: string
  total_ht: number
  total_ttc: number
  statut: 'brouillon' | 'envoyee' | 'en_attente' | 'payee' | 'annulee'
  date_emission: string
  date_echeance: string
}

export interface Devis {
  id: string
  created_at: string
  numero: string
  client_id: string
  total_ht: number
  total_ttc: number
  status: 'brouillon' | 'envoye' | 'accepte' | 'refuse' | 'expire'
  date_emission: string
  date_validite: string
}

export interface Document {
  id: string
  created_at: string
  nom: string
  type: string
  url: string
  taille: number
  client_id: string
}

export interface NotificationLog {
  id: string
  created_at: string
  type: 'email' | 'sms' | 'system'
  titre: string
  message: string
  reference_type: string
  reference_id: string
  metadata: Record<string, unknown> | null
}

export interface ClientAdminAssignment {
  client_id: string
  admin_id: string
  is_owner: boolean
  can_edit: boolean
  can_view: boolean
  assigned_at: string
  admin?: Admin
}

export interface Admin {
  id: string
  full_name: string
  email: string
  avatar_url: string | null
  role: 'admin' | 'super_admin'
  clients_count?: number
}

// Validation schemas
const clientBaseSchema = z.object({
  prenom: z.string().min(2, 'Le prenom doit contenir au moins 2 caracteres'),
  nom: z.string().min(2, 'Le nom doit contenir au moins 2 caracteres'),
  email: z.string().email('Email invalide'),
  phone: z.string().optional().or(z.literal('')),
  adresse: z.string().optional().or(z.literal('')),
  type_client: z.enum(['professionnel', 'particulier']),
})

const clientProSchema = clientBaseSchema.extend({
  type_client: z.literal('professionnel'),
  company: z.string().min(2, 'Le nom de l\'entreprise est requis'),
  siret: z
    .string()
    .regex(/^\d{14}$/, 'Le SIRET doit contenir 14 chiffres')
    .optional()
    .or(z.literal('')),
  tva_intracommunautaire: z.string().optional().or(z.literal('')),
  adresse_facturation: z.string().optional().or(z.literal('')),
})

const clientParticulierSchema = clientBaseSchema.extend({
  type_client: z.literal('particulier'),
  company: z.string().optional().or(z.literal('')),
  siret: z.string().optional().or(z.literal('')),
  tva_intracommunautaire: z.string().optional().or(z.literal('')),
  adresse_facturation: z.string().optional().or(z.literal('')),
})

export const clientSchema = z.discriminatedUnion('type_client', [
  clientProSchema,
  clientParticulierSchema,
])

export type ClientFormData = z.infer<typeof clientSchema>

// Client share interface
export interface ClientShare {
  client_id: string
  admin_id: string
  is_owner: boolean
  can_edit: boolean
  can_view: boolean
}
